import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiAlertCircle, FiChevronLeft, FiChevronRight, FiClock, FiDownload, FiEdit2, FiMinus, FiPlus, FiUploadCloud, FiX } from 'react-icons/fi';
import { formatDateTime } from '../../lib/format';
import { documentError, extensionOf, fetchContent, fetchDocument, formatSize, kindOf, replaceDocument, saveBlob } from '../../lib/documents';
import PdfPages from './PdfPages';

const BAR_BUTTON =
  'inline-flex h-9 flex-none cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full border-0 bg-white/10 px-[14px] text-[13.5px] font-medium text-white transition hover:bg-white/20 disabled:cursor-default disabled:opacity-50';
// The next step after "Modifier": sending the new version.
const BAR_BUTTON_HIGHLIGHT = BAR_BUTTON.replace('bg-white/10', 'bg-brand-600').replace('hover:bg-white/20', 'hover:bg-brand-700');
const ICON_BUTTON = 'flex h-9 w-9 flex-none cursor-pointer items-center justify-center rounded-full border-0 bg-white/10 text-white transition hover:bg-white/20';

// Previous / next arrows over the document (gallery).
const NAV_BUTTON = 'absolute top-1/2 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-0 bg-black/50 text-white transition hover:bg-black/70';

const noMenu = (event) => event.preventDefault();

// Shows a document of the central Dropbox inside the platform, full screen.
// item: { id, name, mimeType }. Downloading is offered only when canDownload.
// onPrevious / onNext (optional): browse a gallery with the arrows and the ← → keys.
const DocumentViewer = ({ item, onClose, canReplace = false, canDownload = false, onReplaced, onPrevious, onNext }) => {
  const kind = kindOf(item.name, item.mimeType);
  const [info, setInfo] = useState(null);
  const [version, setVersion] = useState(null); // null: current content
  const [content, setContent] = useState({ status: 'loading', blob: null, progress: 0, error: '' });
  const [reload, setReload] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pages, setPages] = useState(0);
  const [showVersions, setShowVersions] = useState(false);
  const [busy, setBusy] = useState(null); // { label, progress }
  const [message, setMessage] = useState(null); // { tone, text }
  const picker = useRef(null);

  // Word, PowerPoint and spreadsheets are shown through their conversion (current version only).
  const converted = (kind === 'office' || kind === 'sheet') && version === null;
  const shownAs = converted ? (kind === 'sheet' ? 'html' : 'pdf') : kind;

  useEffect(() => {
    let cancelled = false;
    fetchDocument(item.id)
      .then((data) => !cancelled && setInfo(data))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [item.id, reload]);

  useEffect(() => {
    if ((kind === 'office' || kind === 'sheet') && version !== null) {
      setContent({ status: 'unsupported', blob: null, progress: 0, error: '' });
      return undefined;
    }
    if (kind === 'other') {
      setContent({ status: 'unsupported', blob: null, progress: 0, error: '' });
      return undefined;
    }
    const controller = new AbortController();
    setContent({ status: 'loading', blob: null, progress: 0, error: '' });
    setPages(0);
    fetchContent(item.id, {
      preview: converted,
      version,
      signal: controller.signal,
      onProgress: (ratio) => setContent((current) => (current.status === 'loading' ? { ...current, progress: ratio } : current)),
    })
      .then((blob) => setContent({ status: 'ready', blob, progress: 1, error: '' }))
      .catch(async (error) => {
        if (controller.signal.aborted) return;
        setContent({ status: 'error', blob: null, progress: 0, error: await documentError(error, 'Ce document n’a pas pu être ouvert. Vérifiez la connexion, puis réessayez.') });
      });
    return () => controller.abort();
  }, [item.id, kind, version, converted, reload]);

  const url = useMemo(() => (content.blob && ['image', 'video', 'audio'].includes(shownAs) ? URL.createObjectURL(content.blob) : ''), [content.blob, shownAs]);
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);

  const [html, setHtml] = useState('');
  const [text, setText] = useState('');
  useEffect(() => {
    setHtml('');
    setText('');
    if (!content.blob) return;
    if (shownAs === 'html') content.blob.text().then(setHtml);
    if (shownAs === 'text') content.blob.text().then(setText);
  }, [content.blob, shownAs]);

  useEffect(() => {
    const onKey = (event) => {
      if (busy) return;
      if (event.key === 'Escape') onClose();
      else if (event.key === 'ArrowLeft' && onPrevious) onPrevious();
      else if (event.key === 'ArrowRight' && onNext) onNext();
    };
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [busy, onClose, onPrevious, onNext]);

  const download = async () => {
    setBusy({ label: 'Téléchargement…' });
    setMessage(null);
    try {
      const blob = await fetchContent(item.id, { version, download: true });
      const name = version !== null && info && info.versions[version] ? info.versions[version].name || item.name : item.name;
      saveBlob(blob, name);
    } catch (error) {
      setMessage({ tone: 'error', text: await documentError(error, 'Le téléchargement a échoué.') });
    } finally {
      setBusy(null);
    }
  };

  // Word, Excel, PowerPoint: edited on the device with the usual application, then sent back
  // with "Nouvelle version" (same place, previous version archived).
  const editableOnDevice = canReplace && (kind === 'office' || kind === 'sheet');
  const application = kind === 'sheet' ? 'Excel' : /^(ppt|pptx|pptm|pps|ppsx|ppsm|odp)$/.test(extensionOf(item.name)) ? 'PowerPoint' : 'Word';
  const [editing, setEditing] = useState(false);
  const editOnDevice = async () => {
    setBusy({ label: 'Téléchargement…' });
    setMessage(null);
    try {
      saveBlob(await fetchContent(item.id, { download: true }), item.name);
      setEditing(true);
      setMessage({
        tone: 'guide',
        text: `Le document est sur votre appareil : modifiez-le dans ${application} et enregistrez-le, puis cliquez sur « Nouvelle version » pour le remplacer ici.`,
      });
    } catch (error) {
      setMessage({ tone: 'error', text: await documentError(error, 'Le téléchargement a échoué.') });
    } finally {
      setBusy(null);
    }
  };

  const replace = async (file) => {
    if (!file) return;
    if (extensionOf(file.name) !== extensionOf(item.name)) {
      setMessage({ tone: 'error', text: `La nouvelle version doit être du même type que le document (.${extensionOf(item.name) || '?'}).` });
      return;
    }
    setMessage(null);
    setBusy({ label: 'Envoi de la nouvelle version…', progress: 0 });
    try {
      const updated = await replaceDocument(item.id, file, (ratio) => setBusy({ label: 'Envoi de la nouvelle version…', progress: ratio }));
      setInfo(updated);
      setVersion(null);
      setEditing(false);
      setReload((count) => count + 1);
      setMessage({ tone: 'ok', text: 'Nouvelle version enregistrée. L’ancienne est conservée dans les archives.' });
      if (onReplaced) onReplaced(updated);
    } catch (error) {
      setMessage({ tone: 'error', text: await documentError(error, 'La nouvelle version n’a pas pu être enregistrée.') });
    } finally {
      setBusy(null);
    }
  };

  const versions = info ? info.versions : [];
  const current = info ? { savedBy: info.savedBy, savedAt: info.savedAt } : null;
  const viewing = version !== null && versions[version];
  const subtitle = viewing
    ? `Version du ${formatDateTime(viewing.savedAt)}${viewing.savedBy ? ` · ${viewing.savedBy}` : ''} (archive)`
    : [info && formatSize(info.size), current && current.savedAt && `Enregistré le ${formatDateTime(current.savedAt)}${current.savedBy ? ` par ${current.savedBy}` : ''}`, pages > 1 && `${pages} pages`]
        .filter(Boolean)
        .join(' · ');

  let body;
  if (content.status === 'loading') {
    body = (
      <div className="flex h-full flex-col items-center justify-center gap-[12px] px-[20px]">
        <p className="m-0 text-[14px] text-white/75">Ouverture du document…</p>
        <div className="h-1.5 w-56 overflow-hidden rounded-full bg-white/15">
          <div className="h-full rounded-full bg-white transition-all" style={{ width: `${Math.round((content.progress || 0.05) * 100)}%` }} />
        </div>
      </div>
    );
  } else if (content.status === 'error' || content.status === 'unsupported') {
    body = (
      <div className="flex h-full flex-col items-center justify-center gap-[12px] px-[24px] text-center">
        <FiAlertCircle size={28} aria-hidden="true" className="text-white/60" />
        <p className="m-0 max-w-md text-[15px] leading-relaxed text-white/85">
          {content.status === 'error'
            ? content.error
            : version !== null
              ? 'Les anciennes versions de ce type de document ne s’affichent pas dans la plateforme.'
              : 'Ce type de fichier ne s’affiche pas dans la plateforme.'}
        </p>
        {content.status === 'unsupported' && canDownload && <p className="m-0 text-[13.5px] text-white/60">Vous pouvez le télécharger pour l’ouvrir sur votre appareil.</p>}
        {content.status === 'error' && (
          <button type="button" onClick={() => setReload((count) => count + 1)} className={BAR_BUTTON}>
            Réessayer
          </button>
        )}
      </div>
    );
  } else if (shownAs === 'pdf') {
    body = <PdfPages blob={content.blob} zoom={zoom} onPages={setPages} onError={() => setContent({ status: 'error', blob: null, progress: 0, error: 'Ce PDF n’a pas pu être affiché.' })} />;
  } else if (shownAs === 'html') {
    body = (
      <div className="h-full p-[12px] sm:p-[20px]">
        <iframe title={item.name} sandbox="" srcDoc={html} className="h-full w-full rounded-xl border-0 bg-white" />
      </div>
    );
  } else if (shownAs === 'image') {
    body = (
      <div className="flex min-h-full items-center justify-center p-[16px]">
        <img src={url} alt={item.name} draggable={false} onContextMenu={noMenu} className="block max-w-full select-none object-contain" style={{ maxHeight: zoom === 1 ? 'calc(100vh - 120px)' : undefined, width: zoom === 1 ? undefined : `${zoom * 100}%` }} />
      </div>
    );
  } else if (shownAs === 'video') {
    body = (
      <div className="flex h-full items-center justify-center p-[16px]">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video src={url} controls controlsList="nodownload" disablePictureInPicture onContextMenu={noMenu} className="max-h-full max-w-full rounded-lg bg-black" />
      </div>
    );
  } else if (shownAs === 'audio') {
    body = (
      <div className="flex h-full items-center justify-center p-[16px]">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio src={url} controls controlsList="nodownload" onContextMenu={noMenu} className="w-full max-w-lg" />
      </div>
    );
  } else {
    body = <pre className="m-[16px] whitespace-pre-wrap break-words rounded-xl bg-white p-[20px] text-[14px] leading-relaxed text-ink">{text}</pre>;
  }

  const zoomable = content.status === 'ready' && (shownAs === 'pdf' || shownAs === 'image');

  return (
    <div role="dialog" aria-modal="true" aria-label={item.name} className="fixed inset-0 z-[60] flex flex-col bg-[#1c1c1e] print:hidden">
      <header className="flex flex-none items-center gap-[10px] border-0 border-b-[1px] border-solid border-white/10 px-[12px] py-[10px] sm:px-[20px]">
        <button type="button" onClick={onClose} disabled={Boolean(busy)} aria-label="Fermer" title="Fermer" className={ICON_BUTTON}>
          <FiX size={18} aria-hidden="true" />
        </button>
        <div className="min-w-0 flex-1">
          <h2 className="m-0 truncate text-[15px] font-semibold text-white">{item.name}</h2>
          {subtitle && <p className="m-0 truncate text-[12.5px] text-white/55">{subtitle}</p>}
        </div>
        {zoomable && (
          <div className="hidden items-center gap-1 sm:flex">
            <button type="button" onClick={() => setZoom((value) => Math.max(0.5, value - 0.25))} aria-label="Réduire" title="Réduire" className={ICON_BUTTON}>
              <FiMinus size={16} aria-hidden="true" />
            </button>
            <span className="w-12 text-center text-[13px] tabular-nums text-white/70">{Math.round(zoom * 100)} %</span>
            <button type="button" onClick={() => setZoom((value) => Math.min(3, value + 0.25))} aria-label="Agrandir" title="Agrandir" className={ICON_BUTTON}>
              <FiPlus size={16} aria-hidden="true" />
            </button>
          </div>
        )}
        <button type="button" onClick={() => setShowVersions((open) => !open)} aria-pressed={showVersions} className={BAR_BUTTON} title="Versions du document">
          <FiClock size={15} aria-hidden="true" />
          <span className="hidden sm:inline">Versions</span>
          {versions.length > 0 && <span className="tabular-nums text-white/60">{versions.length + 1}</span>}
        </button>
        {editableOnDevice && version === null && (
          <button type="button" onClick={editOnDevice} disabled={Boolean(busy)} className={BAR_BUTTON} title={`Modifier dans ${application}`}>
            <FiEdit2 size={15} aria-hidden="true" />
            <span className="hidden sm:inline">Modifier</span>
          </button>
        )}
        {canReplace && version === null && (
          <>
            <button
              type="button"
              onClick={() => picker.current && picker.current.click()}
              disabled={Boolean(busy)}
              className={editing ? BAR_BUTTON_HIGHLIGHT : BAR_BUTTON}
              title="Remplacer par une nouvelle version"
            >
              <FiUploadCloud size={15} aria-hidden="true" />
              <span className="hidden sm:inline">Nouvelle version</span>
            </button>
            <input
              ref={picker}
              type="file"
              accept={extensionOf(item.name) ? `.${extensionOf(item.name)}` : undefined}
              className="hidden"
              onChange={(event) => {
                replace(event.target.files[0]);
                event.target.value = '';
              }}
            />
          </>
        )}
        {canDownload && (
          <button type="button" onClick={download} disabled={Boolean(busy)} className={BAR_BUTTON} title="Télécharger">
            <FiDownload size={15} aria-hidden="true" />
            <span className="hidden sm:inline">Télécharger</span>
          </button>
        )}
      </header>

      {(busy || message) && (
        <div
          className={`flex-none px-[16px] py-[10px] text-center text-[13.5px] ${
            message && message.tone === 'error' ? 'bg-red-600/90 text-white' : message && message.tone === 'guide' ? 'bg-brand-600 text-white' : 'bg-white/10 text-white'
          }`}
          role="status"
        >
          {busy ? `${busy.label}${busy.progress !== undefined ? ` ${Math.round(busy.progress * 100)} %` : ''}` : message.text}
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <div className="relative min-w-0 flex-1">
          <div className="h-full overflow-auto" onContextMenu={noMenu}>
            {body}
          </div>
          {onPrevious && (
            <button type="button" onClick={onPrevious} aria-label="Document précédent" title="Précédent (←)" className={`${NAV_BUTTON} left-[12px]`}>
              <FiChevronLeft size={22} aria-hidden="true" />
            </button>
          )}
          {onNext && (
            <button type="button" onClick={onNext} aria-label="Document suivant" title="Suivant (→)" className={`${NAV_BUTTON} right-[12px]`}>
              <FiChevronRight size={22} aria-hidden="true" />
            </button>
          )}
        </div>
        {showVersions && (
          <aside className="absolute inset-x-0 bottom-0 max-h-[60vh] overflow-y-auto rounded-t-3xl bg-white p-[20px] shadow-[0_-12px_40px_rgba(0,0,0,0.35)] sm:static sm:max-h-none sm:w-[320px] sm:flex-none sm:rounded-none sm:shadow-none">
            <div className="mb-[12px] flex items-center justify-between gap-2">
              <h3 className="m-0 text-[15px] font-semibold text-ink">Versions</h3>
              <button type="button" onClick={() => setShowVersions(false)} aria-label="Fermer les versions" className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-0 bg-black/[0.05] text-black/60">
                <FiX size={15} aria-hidden="true" />
              </button>
            </div>
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              <li>
                <button
                  type="button"
                  onClick={() => setVersion(null)}
                  className={`w-full cursor-pointer rounded-xl border-0 px-[12px] py-[10px] text-left ${version === null ? 'bg-brand-50' : 'bg-mist hover:bg-black/[0.06]'}`}
                >
                  <span className="block text-[13.5px] font-semibold text-ink">Version actuelle</span>
                  <span className="block text-[12.5px] text-black/50">
                    {current && current.savedAt ? `${formatDateTime(current.savedAt)}${current.savedBy ? ` · ${current.savedBy}` : ''}` : '—'}
                  </span>
                </button>
              </li>
              {versions
                .slice()
                .reverse()
                .map((entry) => (
                  <li key={entry.index}>
                    <button
                      type="button"
                      disabled={!canDownload}
                      onClick={() => setVersion(entry.index)}
                      className={`w-full rounded-xl border-0 px-[12px] py-[10px] text-left ${version === entry.index ? 'bg-brand-50' : 'bg-mist'} ${canDownload ? 'cursor-pointer hover:bg-black/[0.06]' : 'cursor-default'}`}
                    >
                      <span className="block text-[13.5px] font-medium text-ink">{formatDateTime(entry.savedAt) || 'Date inconnue'}</span>
                      <span className="block text-[12.5px] text-black/50">
                        {[entry.savedBy, formatSize(entry.size)].filter(Boolean).join(' · ')}
                      </span>
                    </button>
                  </li>
                ))}
            </ul>
            <p className="mb-0 mt-[12px] text-[12.5px] leading-snug text-black/45">
              {versions.length === 0
                ? 'Aucune version précédente.'
                : canDownload
                  ? 'Chaque version remplacée est conservée dans les archives du Dropbox du service.'
                  : 'Les versions précédentes sont conservées ; leur consultation est réservée au niveau Administrateur.'}
            </p>
          </aside>
        )}
      </div>
    </div>
  );
};

export default DocumentViewer;
